// P2 sheets (frozen mockups-stk-v2): SCR-STK-03 adjust, -04 count, -05 write-off, -06 repack, -07 opening stock.
// The database decides (balances, reasons, locks); these checks only give instant feedback.
import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  BottomSheet,
  Button,
  Chips,
  Field,
  Input,
  LineAdd,
  List,
  ListRow,
  Notice,
  QuantityInput,
  QuantityToken,
  Select,
  SheetActions,
  Tabs,
} from '../../design/kit';
import { Refusal } from '../../data/auth';
import {
  adjustGarage,
  countGarage,
  postOpeningStock,
  repackGarage,
  writeOffDamaged,
  type InventoryRow,
  type StockLine,
} from '../../data/stock';
import { Name } from '../../i18n/Isolate';
import { messageKey } from '../../lib/errors';
import { parseAmount } from '../catalog/common';

export const STOCK = ['stock'] as const;
const REASONS = ['reason.damage', 'reason.loss', 'reason.error', 'reason.found', 'reason.sample'];

/** Whole non-negative number or null; '' counts as 0. */
function whole(text: string): number | null {
  if (!text.trim()) return 0;
  return /^\d+$/.test(text.trim()) ? Number(text.trim()) : null;
}

/** "1 box" / "2 boxes": the singular for one (design system: QuantityToken). */
export function countWord(t: TFunction, unit: 'box' | 'piece', n: number): string {
  return t(Math.abs(n) === 1 ? `${unit}.one` : `${unit}.n`, { n });
}

export function countLines(t: TFunction, n: number): string {
  return t(n === 1 ? 'lines.one' : 'lines.n', { n });
}

export function useStockWords() {
  const { t } = useTranslation();
  const qty = (r: { box_capacity: number | null }, boxes: number, loose: number) => (
    <QuantityToken
      boxes={boxes}
      loose={loose}
      boxed={r.box_capacity !== null}
      boxLabel={t(boxes === 1 ? 'box.one' : 'box.n', { n: '' }).trim()}
      pieceLabel={t(loose === 1 ? 'piece.one' : 'piece.n', { n: '' }).trim()}
    />
  );
  const signed = (r: { box_capacity: number | null }, boxes: number, loose: number) =>
    [
      boxes ? `${boxes > 0 ? '+' : '−'}${countWord(t, 'box', Math.abs(boxes))}` : '',
      loose ? `${loose > 0 ? '+' : '−'}${countWord(t, 'piece', Math.abs(loose))}` : '',
    ]
      .filter(Boolean)
      .join(' ') || (r.box_capacity !== null ? t('box.n', { n: 0 }) : t('piece.n', { n: 0 }));
  const refusal = (err: unknown) => {
    if (!(err instanceof Refusal)) return t(messageKey('SERVER_ERROR'));
    const d = err.detail;
    const b =
      d.boxes !== undefined
        ? `${Number(d.boxes) ? t('box.n', { n: Number(d.boxes) }) + ' + ' : ''}${t('piece.n', { n: Number(d.loose ?? 0) })}`
        : '';
    return t(messageKey(err.code), { b, n: String(d.max ?? ''), p: String(d.product ?? '') });
  };
  return { qty, signed, refusal };
}

function ProductPick({
  rows,
  value,
  onChange,
  label,
}: {
  rows: InventoryRow[];
  value: string;
  onChange: (id: string) => void;
  label: string;
}) {
  const { t } = useTranslation();
  return (
    <Field label={label}>
      {({ id }) => (
        <Select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">{t('none')}</option>
          {rows.map((r) => (
            <option key={r.product_id} value={r.product_id}>
              {r.name} — {r.box_capacity !== null ? `${t('box.n', { n: r.boxes })} + ` : ''}
              {t('piece.n', { n: r.loose })}
            </option>
          ))}
        </Select>
      )}
    </Field>
  );
}

export function ReasonField({
  value,
  onChange,
  error,
  keys = REASONS,
}: {
  value: string;
  onChange: (v: string) => void;
  error?: string | undefined;
  keys?: string[];
}) {
  const { t } = useTranslation();
  return (
    <Field label={t('reason')} error={error}>
      {({ id, describedBy, invalid }) => (
        <>
          <Input
            id={id}
            dir="auto"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            aria-describedby={describedBy}
            aria-invalid={invalid}
          />
          <Chips
            label={t('reason')}
            items={keys.map((k) => ({ id: k, label: t(k) }))}
            selected={keys.filter((k) => t(k) === value)}
            onToggle={(k) => onChange(t(k))}
          />
        </>
      )}
    </Field>
  );
}

export function useDone(onClose: () => void) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: STOCK });
    void qc.invalidateQueries({ queryKey: ['catalog'] });
    void qc.invalidateQueries({ queryKey: ['rounds'] });
    onClose();
  };
}

/** SCR-STK-03: adjust one garage product up or down with a reason (owner, Storekeeper). */
export function AdjustSheet({ garage, onClose }: { garage: InventoryRow[]; onClose: () => void }) {
  const { t } = useTranslation();
  const words = useStockWords();
  const done = useDone(onClose);
  const [product, setProduct] = useState('');
  const [direction, setDirection] = useState<'increase' | 'decrease'>('decrease');
  const [boxes, setBoxes] = useState('');
  const [loose, setLoose] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<{ field: 'qty' | 'reason' | 'form'; text: string }>();
  const row = garage.find((r) => r.product_id === product);
  const b = whole(boxes);
  const l = whole(loose);
  const sign = direction === 'increase' ? 1 : -1;
  const after = row && b !== null && l !== null ? { boxes: row.boxes + sign * b, loose: row.loose + sign * l } : null;
  const save = useMutation({
    mutationFn: () => adjustGarage(product, direction, b ?? 0, l ?? 0, reason.trim()),
    onSuccess: done,
    onError: (e) =>
      setError({
        field: e instanceof Refusal && e.code === 'REASON_REQUIRED' ? 'reason' : 'qty',
        text: words.refusal(e),
      }),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!row) return setError({ field: 'form', text: t('err.required') });
    if (b === null || l === null || b + l === 0) return setError({ field: 'qty', text: t('err.qty') });
    if (b > 0 && row.box_capacity === null) return setError({ field: 'qty', text: t('err.boxloose') });
    if (after && (after.boxes < 0 || after.loose < 0)) {
      return setError({
        field: 'qty',
        text: t('err.overbalance', {
          b: `${row.box_capacity !== null ? t('box.n', { n: row.boxes }) + ' + ' : ''}${t('piece.n', { n: row.loose })}`,
        }),
      });
    }
    if (!reason.trim()) return setError({ field: 'reason', text: t('err.reason') });
    setError(undefined);
    save.mutate();
  };
  return (
    <BottomSheet title={t('s23.title')} open onClose={onClose} busy={save.isPending}>
      <form onSubmit={submit} noValidate className="yv-skeleton-stack">
        {error?.field === 'form' && <Notice tone="danger">{error.text}</Notice>}
        <ProductPick rows={garage} value={product} onChange={setProduct} label={t('product')} />
        <Tabs
          label={t('quantity')}
          value={direction}
          onChange={(v) => setDirection(v as 'increase' | 'decrease')}
          items={[
            { id: 'increase', label: t('dir.increase') },
            { id: 'decrease', label: t('dir.decrease') },
          ]}
        />
        <Field
          label={t('quantity')}
          error={error?.field === 'qty' ? error.text : undefined}
          hint={
            row ? (
              <>
                {t('balance.before')} {words.qty(row, row.boxes, row.loose)}
                {after && after.boxes >= 0 && after.loose >= 0 && (
                  <>
                    {' → '}
                    {words.qty(row, after.boxes, after.loose)}
                  </>
                )}
              </>
            ) : undefined
          }
        >
          {({ describedBy, invalid }) => (
            <QuantityInput
              boxes={boxes}
              loose={loose}
              boxed={row?.box_capacity != null}
              onBoxes={setBoxes}
              onLoose={setLoose}
              boxLabel={t('qty.boxes')}
              pieceLabel={t('qty.pieces')}
              invalid={invalid}
              describedBy={describedBy}
            />
          )}
        </Field>
        <ReasonField value={reason} onChange={setReason} error={error?.field === 'reason' ? error.text : undefined} />
        <SheetActions>
          <Button type="submit" variant="main" busy={save.isPending}>
            {t('adjust.save')}
          </Button>
          <Button onClick={onClose}>{t('cancel')}</Button>
        </SheetActions>
      </form>
    </BottomSheet>
  );
}

export interface Line {
  row: InventoryRow;
  boxes: number;
  loose: number;
  reason: string;
  cost?: number;
}

/** The dashed add-line block plus the lines (design system: LineList). */
export function LineSheet({
  title,
  header,
  rows,
  lines,
  setLines,
  renderSub,
  extra,
  validateLine,
  countMode,
  submitLabel,
  summary,
  onSubmit,
  busy,
  error,
  onClose,
}: {
  title: string;
  /** Controls above the line being prepared (direction, truck). */
  header?: ReactNode;
  rows: InventoryRow[];
  lines: Line[];
  setLines: (l: Line[]) => void;
  renderSub: (line: Line) => ReactNode;
  extra: (state: {
    reason: string;
    setReason: (v: string) => void;
    cost: string;
    setCost: (v: string) => void;
    row: InventoryRow | undefined;
    boxes: number | null;
    loose: number | null;
  }) => ReactNode;
  validateLine: (row: InventoryRow, boxes: number, loose: number, reason: string, cost: number | null) => string | null;
  countMode?: boolean;
  submitLabel: string;
  /** A muted line above the actions (lines, differences) — the button keeps its plain verb. */
  summary?: string | undefined;
  onSubmit: () => void;
  busy: boolean;
  error: string | undefined;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const words = useStockWords();
  const [product, setProduct] = useState('');
  const [boxes, setBoxes] = useState('');
  const [loose, setLoose] = useState('');
  const [reason, setReason] = useState('');
  const [cost, setCost] = useState('');
  const [lineError, setLineError] = useState<string>();
  const row = rows.find((r) => r.product_id === product);
  const b = whole(boxes);
  const l = whole(loose);
  const add = () => {
    if (!row) return setLineError(t('err.required'));
    if (b === null || l === null) return setLineError(t('err.qty'));
    if (b > 0 && row.box_capacity === null) return setLineError(t('err.boxloose'));
    const problem = validateLine(row, b, l, reason.trim(), parseAmount(cost));
    if (problem) return setLineError(problem);
    setLineError(undefined);
    const rest = lines.filter((x) => x.row.product_id !== row.product_id);
    const same = lines.find((x) => x.row.product_id === row.product_id);
    // The same product again adds to its line (count: the new count replaces it).
    const merged =
      same && !countMode
        ? { ...same, boxes: same.boxes + b, loose: same.loose + l }
        : { row, boxes: b, loose: l, reason: reason.trim() };
    const next: Line = { ...merged, reason: reason.trim() || (same?.reason ?? '') };
    const c = parseAmount(cost);
    if (c !== null) next.cost = c;
    setLines([...rest, next]);
    setProduct('');
    setBoxes('');
    setLoose('');
    setReason('');
    setCost('');
  };
  return (
    <BottomSheet title={title} open onClose={onClose} busy={busy}>
      {error && <Notice tone="danger">{error}</Notice>}
      {header}
      <LineAdd>
        <ProductPick rows={rows} value={product} onChange={setProduct} label={t('product')} />
        <Field label={countMode ? t('line.counted') : t('quantity')} error={lineError}>
          {({ describedBy, invalid }) => (
            <QuantityInput
              boxes={boxes}
              loose={loose}
              boxed={row?.box_capacity != null}
              onBoxes={setBoxes}
              onLoose={setLoose}
              boxLabel={t('qty.boxes')}
              pieceLabel={t('qty.pieces')}
              invalid={invalid}
              describedBy={describedBy}
            />
          )}
        </Field>
        {extra({ reason, setReason, cost, setCost, row, boxes: b, loose: l })}
        <Button icon="plus" onClick={add}>
          {t('line.add')}
        </Button>
      </LineAdd>
      {lines.length > 0 && (
        <List label={title} animateNew>
          {lines.map((x) => (
            <ListRow
              key={x.row.product_id}
              testId={`line-${x.row.sku ?? x.row.product_id}`}
              title={<Name>{x.row.name}</Name>}
              sub={renderSub(x)}
              value={words.qty(x.row, x.boxes, x.loose)}
              actions={
                <Button pill variant="quiet" onClick={() => setLines(lines.filter((y) => y !== x))}>
                  {t('line.remove')}
                </Button>
              }
            />
          ))}
        </List>
      )}
      {summary && lines.length > 0 && <p className="yv-auth__lead">{summary}</p>}
      <SheetActions>
        <Button variant="main" busy={busy} onClick={() => (lines.length ? onSubmit() : setLineError(t('err.nolines')))}>
          {submitLabel}
        </Button>
        <Button onClick={onClose}>{t('cancel')}</Button>
      </SheetActions>
    </BottomSheet>
  );
}

/** SCR-STK-04: count garage products; only counted lines post; each difference needs a reason. */
export function CountSheet({ garage, onClose }: { garage: InventoryRow[]; onClose: () => void }) {
  const { t } = useTranslation();
  const words = useStockWords();
  const done = useDone(onClose);
  const [lines, setLines] = useState<Line[]>([]);
  const [error, setError] = useState<string>();
  const differs = (x: { row: InventoryRow; boxes: number; loose: number }) =>
    x.boxes !== x.row.boxes || x.loose !== x.row.loose;
  const save = useMutation({
    mutationFn: () =>
      countGarage(
        lines.map(
          (x): StockLine => ({
            product: x.row.product_id,
            boxes: x.boxes,
            loose: x.loose,
            reason: x.reason || undefined,
          }),
        ),
      ),
    onSuccess: done,
    onError: (e) => setError(words.refusal(e)),
  });
  const withDiff = lines.filter(differs).length;
  return (
    <LineSheet
      title={t('s24.title')}
      rows={garage}
      lines={lines}
      setLines={setLines}
      countMode
      busy={save.isPending}
      error={error}
      onClose={onClose}
      submitLabel={t('count.save')}
      summary={`${countLines(t, lines.length)}${withDiff ? ` · ${t('lines.diff', { n: withDiff })}` : ''}`}
      onSubmit={() => save.mutate()}
      validateLine={(row, b, l, reason) =>
        (b !== row.boxes || l !== row.loose) && !reason ? t('err.reasondiff') : null
      }
      extra={({ reason, setReason, row, boxes, loose }) =>
        row && boxes !== null && loose !== null && (boxes !== row.boxes || loose !== row.loose) ? (
          <ReasonField value={reason} onChange={setReason} />
        ) : null
      }
      renderSub={(x) => (
        <>
          {t('line.system')} {words.signed(x.row, x.row.boxes, x.row.loose).replace(/\+/g, '')} → {t('line.counted')}{' '}
          {words.signed(x.row, x.boxes, x.loose).replace(/\+/g, '')} · {t('line.diff')}{' '}
          {differs(x) ? words.signed(x.row, x.boxes - x.row.boxes, x.loose - x.row.loose) : '0'}
          {x.reason && ` · ${x.reason}`}
        </>
      )}
    />
  );
}

/** SCR-STK-05: write off damaged stock with a reason per line (owner, Storekeeper). */
export function WriteOffSheet({ damaged, onClose }: { damaged: InventoryRow[]; onClose: () => void }) {
  const { t } = useTranslation();
  const words = useStockWords();
  const done = useDone(onClose);
  const [lines, setLines] = useState<Line[]>([]);
  const [error, setError] = useState<string>();
  const save = useMutation({
    mutationFn: () =>
      writeOffDamaged(
        lines.map((x): StockLine => ({ product: x.row.product_id, boxes: x.boxes, loose: x.loose, reason: x.reason })),
      ),
    onSuccess: done,
    onError: (e) => setError(words.refusal(e)),
  });
  return (
    <LineSheet
      title={t('s25.title')}
      rows={damaged.filter((r) => r.calc_pieces > 0)}
      lines={lines}
      setLines={setLines}
      busy={save.isPending}
      error={error}
      onClose={onClose}
      submitLabel={t('writeoff.save')}
      onSubmit={() => save.mutate()}
      validateLine={(row, b, l, reason) => {
        if (b + l === 0) return t('err.qty');
        if (b > row.boxes || l > row.loose)
          return t('err.overbalance', {
            b: `${row.box_capacity !== null ? t('box.n', { n: row.boxes }) + ' + ' : ''}${t('piece.n', { n: row.loose })}`,
          });
        return reason ? null : t('err.reason');
      }}
      extra={({ reason, setReason }) => (
        <ReasonField value={reason} onChange={setReason} keys={['reason.damage', 'reason.expired']} />
      )}
      renderSub={(x) => `${t('reason')}: ${x.reason}`}
    />
  );
}

/** SCR-STK-07: opening stock at go-live (owner), each line with its cost per piece (ADR-094). */
export function OpeningSheet({ products, onClose }: { products: InventoryRow[]; onClose: () => void }) {
  const { t } = useTranslation();
  const words = useStockWords();
  const done = useDone(onClose);
  const [lines, setLines] = useState<Line[]>([]);
  const [error, setError] = useState<string>();
  const save = useMutation({
    mutationFn: () =>
      postOpeningStock(
        lines.map((x): StockLine => ({ product: x.row.product_id, boxes: x.boxes, loose: x.loose, cost: x.cost })),
      ),
    onSuccess: done,
    onError: (e) => setError(words.refusal(e)),
  });
  return (
    <LineSheet
      title={t('s27.title')}
      rows={products}
      lines={lines}
      setLines={setLines}
      busy={save.isPending}
      error={error}
      onClose={onClose}
      submitLabel={t('opening.save')}
      summary={countLines(t, lines.length)}
      onSubmit={() => save.mutate()}
      validateLine={(_row, b, l, _reason, cost) =>
        b + l === 0 ? t('err.qty') : cost === null || cost <= 0 ? t('err.cost') : null
      }
      extra={({ cost, setCost }) => (
        <Field label={t('cost.piece')} hint={t('cost.hint')}>
          {({ id, describedBy }) => (
            <Input
              id={id}
              numeric
              inputMode="decimal"
              value={cost}
              onChange={(e) => setCost(e.target.value)}
              aria-describedby={describedBy}
            />
          )}
        </Field>
      )}
      renderSub={(x) => `${t('cost.piece')} ${x.cost?.toFixed(2) ?? ''}`}
    />
  );
}

/** SCR-STK-06: loose pieces of a boxed product into complete boxes (garage only). */
export function RepackSheet({ garage, onClose }: { garage: InventoryRow[]; onClose: () => void }) {
  const { t } = useTranslation();
  const words = useStockWords();
  const done = useDone(onClose);
  const boxed = useMemo(
    () => garage.filter((r) => r.box_capacity !== null && r.loose >= (r.box_capacity ?? 0)),
    [garage],
  );
  const [product, setProduct] = useState('');
  const [boxes, setBoxes] = useState('1');
  const [error, setError] = useState<string>();
  const row = boxed.find((r) => r.product_id === product);
  const n = whole(boxes);
  const cap = row?.box_capacity ?? 0;
  const max = row ? Math.floor(row.loose / cap) : 0;
  const save = useMutation({
    mutationFn: () => repackGarage(product, n ?? 0),
    onSuccess: done,
    onError: (e) => setError(words.refusal(e)),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!row) return setError(t('err.required'));
    if (!n) return setError(t('err.qty'));
    if (n > max) return setError(t('err.notenough', { n: max }));
    setError(undefined);
    save.mutate();
  };
  return (
    <BottomSheet title={t('s26.title')} open onClose={onClose} busy={save.isPending}>
      <form onSubmit={submit} noValidate className="yv-skeleton-stack">
        <ProductPick rows={boxed} value={product} onChange={setProduct} label={t('product')} />
        <Field label={t('repack.boxes')} hint={row ? t('repack.hint', { c: cap }) : undefined} error={error}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              numeric
              inputMode="numeric"
              value={boxes}
              onChange={(e) => setBoxes(e.target.value)}
              aria-describedby={describedBy}
              aria-invalid={invalid}
            />
          )}
        </Field>
        {row && n !== null && n > 0 && n <= max && (
          <Notice tone="info">
            {t('balance.before')} {words.qty(row, row.boxes, row.loose)} →{' '}
            {words.qty(row, row.boxes + n, row.loose - n * cap)}
          </Notice>
        )}
        <SheetActions>
          <Button type="submit" variant="main" busy={save.isPending}>
            {t('repack.save')}
          </Button>
          <Button onClick={onClose}>{t('cancel')}</Button>
        </SheetActions>
      </form>
    </BottomSheet>
  );
}
